import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { ComponentPropsWithRef } from 'react';

import classnames from '../utils/classnames.tsx';
import { withIcon } from './Icon.tsx';

const ChevronIcon = withIcon(faChevronRight);

interface DisclosureProps extends Omit<ComponentPropsWithRef<'details'>, 'title' | 'onToggle'> {
    title: React.ReactNode;
    summary?: React.ReactNode;
    aside?: React.ReactNode;
    onOpenChange?: (isOpen: boolean) => void;
}

const Disclosure = ({ title, summary, aside, onOpenChange, className, children, ...props }: DisclosureProps) => (
    <details
        {...props}
        onToggle={(event) => onOpenChange?.(event.currentTarget.open)}
        className={classnames('border-0 border-b border-solid border-line-weak last:border-b-0', className)}
    >
        <summary className="flex items-start gap-3 px-4 py-3 cursor-pointer list-none rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-brand-strong">
            {/* keyed to the own details element, not group-open: a nested Disclosure must not follow its parent */}
            <ChevronIcon className="mt-1 text-xs text-content-weak transition-transform [[open]>summary>&]:rotate-90" />
            <span className="flex flex-1 min-w-0 flex-col gap-1.5">
                <span className="flex items-baseline gap-3">
                    <span className="flex-1 text-sm font-medium text-content-strong">{title}</span>
                    {aside && <span className="text-xs text-content-medium">{aside}</span>}
                </span>
                {summary && <span className="text-xs text-content-medium [[open]>summary_&]:hidden">{summary}</span>}
            </span>
        </summary>
        <div className="flex flex-col gap-1 px-3 pb-3">{children}</div>
    </details>
);

const DisclosureGroup = ({ className, ...props }: ComponentPropsWithRef<'div'>) => (
    <div
        {...props}
        className={classnames('flex flex-col rounded-lg border border-solid border-line-weak', className)}
    />
);

export default Object.assign(Disclosure, { Group: DisclosureGroup });
